import { useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { debounce, isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { listProjectCustomData } from '~/api/projectCustomData';
import colors, { PROJECT_AREA_LINE_COLOR } from '~/colors';
import { GeoItemMarkerDot } from '~/components2/Map/GeoItemMarkerDot';
import { customDataQueryKey, customDataRefetchInterval } from '~/pages/projectDetails/filesAndMedia/CustomDataSection';
import { buildCustomDataLegendSections } from '~/pages/projectDetails/map/customDataLayers.helpers';
import { useSiteDataLegendSection } from '~/pages/projectDetails/map/useSiteDataLegendSection';
import layerStore from '~/store2/layers';
import { TOCLabelMap } from '~/store2/TOCLayers';
import { mapState as mapViewState } from '../../Map';
import { METADATA_SIDEBAR_WIDTH } from '../MetadataSidebar/MetadataSidebar';
import { getCurrentlyVisibleLegendLayerIds } from './getCurrentlyVisibleLegendLayerIds';
import { getLegendHeading } from './legendHeading';

export const LEGEND_PANEL_ID = 'map-legend-panel';

type LegendProps = {
  // When the metadata sidebar is docked (360px, right-0), shift left so it isn't covered (CG-855).
  metadataSidebarOpen?: boolean;
};

export const Legend = ({ metadataSidebarOpen }: LegendProps) => {
  // Source projectId from the route (like Surface/CustomDataLayers) — the Map's optional
  // projectId prop isn't populated on the project map route, so a prop would be undefined.
  const { projectId } = useParams({ strict: false }) as { projectId?: string };
  const { isLegendVisible, tilesConfig, visibleLayerIds, projectLayer, visibleLinkedOrganizationIds } = useSnapshot(
    layerStore,
  ) as typeof layerStore;
  const panelRef = useRef<HTMLElement>(null);
  // The panel has no focusable descendants, so onFocus/onBlur alone tracks whether it holds
  // focus; a future focusable child would need to also ignore focus moving between siblings.
  const isFocusWithinPanel = useRef(false);

  useEffect(() => {
    if (!isLegendVisible) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    // The onboarding tour opens the legend programmatically and must keep its own focus;
    // only take focus when the user opened it from the control that points at this panel.
    if (previouslyFocused?.getAttribute('aria-controls') !== LEGEND_PANEL_ID) return;
    panelRef.current?.focus();
    // Hand focus back only while the panel still holds it, so a close that happens after the
    // user or the tour moved on leaves focus where they are now.
    return () => {
      if (isFocusWithinPanel.current) previouslyFocused?.focus?.();
    };
  }, [isLegendVisible]);

  // Cached query, so reading rows here hits the same TanStack cache CustomDataLayers already populated.
  const showCustomData = !!projectId;
  const { data: customDataRows } = useQuery({
    enabled: showCustomData,
    refetchOnWindowFocus: false,
    queryKey: customDataQueryKey(projectId ?? ''),
    queryFn: () => listProjectCustomData(projectId ?? ''),
    // Poll processing→ready even when the files panel (the other poller) isn't mounted.
    refetchInterval: customDataRefetchInterval,
  });
  // Memoized so it stays a stable reference — otherwise the `els` memo (which depends on it)
  // would recompute on every render (e.g. each map pan/zoom).
  const customDataSections = useMemo(
    () => (showCustomData ? buildCustomDataLegendSections(customDataRows, visibleLayerIds) : []),
    [showCustomData, customDataRows, visibleLayerIds],
  );

  // Site Data overlays (Potholes + Field Notes) — shared with the CivilDraw print view.
  const siteDataSection = useSiteDataLegendSection();

  const mapState = useSnapshot(mapViewState) as typeof mapViewState;
  const { current: map } = useMap();
  const [currentlyVisibleLayerIds, setCurrentlyVisibleLayerIds] = useState<string[]>([]);

  function _refreshLegendList() {
    if (!map) {
      return;
    }
    setCurrentlyVisibleLayerIds(getCurrentlyVisibleLegendLayerIds(map));
  }

  const refreshLegendList = debounce(_refreshLegendList, 250);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    const timeout = setTimeout(refreshLegendList, 500);

    return () => {
      // cancel in-progress debounced function (prevents memory leaks)
      refreshLegendList.cancel();
      clearTimeout(timeout);
    };
  }, [isLegendVisible, mapState, visibleLayerIds]);

  const els = useMemo(() => {
    const rowStyle = 'my-2 flex flex-row items-center space-x-4';
    const topItems: React.ReactNode[] = [];

    // Custom-data shapefile layers: one section per visible layer, one row per CateTitle. The swatch shape follows the geometry — dash (line), filled rect (polygon), dot (point) — colored from the shared symbology.
    const customDataItems = customDataSections.map((section) => (
      <div key={section.layerId} data-testid={`legend-custom-data-section-${section.layerId}`}>
        <div className="font-bold">{section.label}</div>
        {section.items.map((item) => (
          <div
            key={item.value}
            className={rowStyle}
            data-testid={`legend-custom-data-item-${section.layerId}-${item.value}`}
          >
            {item.geometryType === 'polygon' ? (
              <div
                style={{ width: 14, height: 10, backgroundColor: item.color, border: '1px solid rgba(0,0,0,0.25)' }}
              />
            ) : item.geometryType === 'point' ? (
              <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
            ) : (
              <div style={{ borderTop: `3px solid ${item.color}`, width: 20 }} />
            )}
            <div className="text-xs">{item.label}</div>
          </div>
        ))}
      </div>
    ));

    // Site Data dot rows (Potholes / Photos & Videos / Notes) — only the types present on the
    // map. Reuses GeoItemMarkerDot so the legend dot matches the on-map marker.
    const siteDataItems = siteDataSection
      ? [
          <div key="site-data" data-testid="legend-site-data-section">
            <div className="font-bold">{siteDataSection.label}</div>
            {siteDataSection.items.map((item) => (
              <div key={item.key} className={rowStyle} data-testid={`legend-site-data-item-${item.key}`}>
                <GeoItemMarkerDot fill={item.fill} stroke={item.stroke} dotSize={14} />
                <div className="text-xs">{item.label}</div>
              </div>
            ))}
          </div>,
        ]
      : [];

    if (projectLayer) {
      topItems.push(
        <div key="project">
          <div className={rowStyle}>
            <div style={{ border: `3px dashed ${PROJECT_AREA_LINE_COLOR}`, width: 20, height: 20 }} />
            <div className="text-xs">Project Area</div>
          </div>
        </div>,
      );
    }

    if (visibleLinkedOrganizationIds.size > 0) {
      topItems.push(
        <div key="partner-org">
          <div className={rowStyle}>
            <div style={{ border: `3px dotted ${colors.blue50}`, opacity: 0.75, width: 20, height: 20 }} />
            <div className="text-xs">Linked Organization Projects</div>
          </div>
        </div>,
      );
    }

    if (!isEmpty(currentlyVisibleLayerIds)) {
      const tileItems = currentlyVisibleLayerIds.flatMap((l) => {
        // tilesConfig is optional and undefined during the async /v1/tiles/config load window.
        // A persisted-visible layer (e.g. fiberLitBuildings) can land in currentlyVisibleLayerIds
        // before the config resolves, so guard the parent deref instead of asserting non-null.
        const config = tilesConfig?.[l];
        if (!config) return [];
        const items = config.legend.map((l, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: entries have no natural id and this config's order is fixed
          <div key={`${config.id}-${i}`} className={rowStyle}>
            <img src={`data:image/jpeg;base64,${l.imageData}`} width={l.width} height={l.height} alt="" />
            <div className="text-xs">{l.label || TOCLabelMap.get(config.id) || config.name}</div>
          </div>
        ));
        return (
          <div key={l}>
            <div className="font-bold">{getLegendHeading(config)}</div>
            {items}
          </div>
        );
      });
      return [...topItems, ...tileItems, ...customDataItems, ...siteDataItems];
    } else {
      const rest = [...topItems, ...customDataItems, ...siteDataItems];
      if (rest.length > 0) return rest;
      return <div className="text-xs">Zoom in to see data</div>;
    }
  }, [
    currentlyVisibleLayerIds,
    projectLayer,
    visibleLinkedOrganizationIds,
    tilesConfig,
    customDataSections,
    siteDataSection,
  ]);

  if (isLegendVisible) {
    return (
      <section
        ref={panelRef}
        id={LEGEND_PANEL_ID}
        aria-label="Legend"
        tabIndex={-1}
        onFocus={() => {
          isFocusWithinPanel.current = true;
        }}
        onBlur={() => {
          isFocusWithinPanel.current = false;
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') layerStore.toggleLegend(false);
        }}
        data-testid="map-legend"
        // right-14 (56px) default gutter, plus the sidebar's width when docked — clamped via min()
        // to track the sidebar's own max-w-[85%] clamp on narrow screens (CG-855).
        style={{ right: metadataSidebarOpen ? `calc(min(${METADATA_SIDEBAR_WIDTH}px, 85%) + 56px)` : 56 }}
        className="absolute bottom-8 z-50 max-h-[75%] w-[20vw] overflow-y-auto rounded bg-white p-4 shadow-sm"
      >
        {els}
      </section>
    );
  } else {
    return null;
  }
};
